import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type KeyboardEvent } from "react";

import { FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import { flowsKey, getFlows, getSequence, JourneyError, screenUrl, sequenceKey, type Flows, type Sequence, type Step } from "./api";
import {
  caption,
  chosenVars,
  defaultVars,
  FLOW,
  flowLabel,
  notCapturedText,
  pickFlow,
  SEQUENCE_ERROR_TITLE,
  tapLine,
  variableLabel,
} from "./format";
import { isNoRelease, JourneyHeader, LoadError, NoRelease, useJourneySearch } from "./parts";

// C5 Sequence view (dev spec 4070402f; UX design §3 F1, §4, §5). Pick a flow and the answers a person
// gives, and see the screens the current production build shows, in order, with the tap that leaves each.

function flowSpec(flows: Flows): FilterSpec {
  return {
    name: FLOW,
    label: "Flow",
    kind: "choice",
    options: Object.entries(flows.flows).map(([key, flow]) => ({ value: key, label: flowLabel(key, flow) })),
  };
}

function variableSpecs(flows: Flows, flow: string | null): FilterSpec[] {
  const variables = flow ? flows.flows[flow]?.variables ?? {} : {};
  return Object.entries(variables).map(([name, values]) => ({
    name,
    label: variableLabel(name),
    kind: "choice",
    anyLabel: "Not asked",
    options: values.map((value) => ({ value, label: value })),
  }));
}

export function SequenceTab() {
  const { values, set } = useJourneySearch();
  const flowsQuery = useQuery({ queryKey: flowsKey, queryFn: getFlows, retry: false });
  const flows = flowsQuery.data?.data;
  const flow = pickFlow(flows, typeof values[FLOW] === "string" ? (values[FLOW] as string) : null);
  const flowDef = flow && flows ? flows.flows[flow] : undefined;
  const vars = chosenVars(flow, flowDef, values);

  const sequenceQuery = useQuery({
    queryKey: sequenceKey(flow ?? "", vars),
    queryFn: () => getSequence(flow as string, vars),
    enabled: Boolean(flow),
    retry: false,
  });
  // The not-captured panel keeps the last strip that loaded on screen (UX F1 recovery): a rejected
  // query has no data of its own, so the last good one is held here.
  const [lastGood, setLastGood] = useState<Sequence | null>(null);
  const loaded = sequenceQuery.data?.data;
  useEffect(() => {
    if (loaded) setLastGood(loaded);
  }, [loaded]);

  if (flowsQuery.isError && isNoRelease(flowsQuery.error)) {
    return (
      <div className="flex flex-col gap-4">
        <JourneyHeader />
        <NoRelease />
      </div>
    );
  }
  if (flowsQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        <JourneyHeader />
        <LoadError title={SEQUENCE_ERROR_TITLE} error={flowsQuery.error} retry={() => void flowsQuery.refetch()} />
      </div>
    );
  }
  if (!flows) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <JourneyHeader />
        <StripSkeleton />
      </div>
    );
  }

  const onChange = (next: Record<string, unknown>) => {
    const nextFlow = typeof next[FLOW] === "string" ? (next[FLOW] as string) : flow;
    if (nextFlow !== flow) {
      // A new flow resets its answers to that flow's defaults (UX §6 Flow picker).
      const cleared = Object.fromEntries(Object.keys(flowDef?.variables ?? {}).map((n) => [n, null]));
      set({ ...cleared, [FLOW]: nextFlow, ...defaultVars(nextFlow ? flows.flows[nextFlow] : undefined) });
      return;
    }
    set({ ...(next as Record<string, string | null>), [FLOW]: flow });
  };

  const error = sequenceQuery.error;
  const notCaptured = error instanceof JourneyError && error.key === "not_captured";
  const shown = error ? (notCaptured ? lastGood : null) : loaded ?? null;
  const sameSelection = Boolean(shown && !error && shown.flow === flow);

  return (
    <div className="flex flex-col gap-4">
      <JourneyHeader drift={sameSelection && shown ? shown.drift : []} />
      <FilterBar layout="segmented" filters={[flowSpec(flows)]} values={{ [FLOW]: flow }} onChange={onChange} />
      <FilterBar
        layout="stacked"
        filters={variableSpecs(flows, flow)}
        values={{ ...Object.fromEntries(Object.keys(flowDef?.variables ?? {}).map((n) => [n, vars[n] ?? null])) }}
        onChange={onChange}
      />
      {notCaptured && (
        <Alert role="status">
          <AlertTitle>Not captured</AlertTitle>
          <AlertDescription>{notCapturedText(flows.release, flowDef?.pending)}</AlertDescription>
        </Alert>
      )}
      {error && !notCaptured &&
        (isNoRelease(error) ? (
          <NoRelease />
        ) : (
          <LoadError title={SEQUENCE_ERROR_TITLE} error={error} retry={() => void sequenceQuery.refetch()} />
        ))}
      {sequenceQuery.isLoading && <StripSkeleton />}
      {shown && <Strip sequence={shown} />}
    </div>
  );
}

function StripSkeleton() {
  return (
    <div className="flex gap-3 overflow-hidden" aria-busy="true">
      <p role="status" className="sr-only">
        Loading the sequence…
      </p>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="h-72 w-40 shrink-0 rounded-ks-md" />
      ))}
    </div>
  );
}

function Strip({ sequence }: { sequence: Sequence }) {
  const [open, setOpen] = useState<number | null>(null);
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const to = e.key === "ArrowRight" ? i + 1 : e.key === "ArrowLeft" ? i - 1 : null;
    if (to === null) return;
    e.preventDefault();
    document.getElementById(`aj-step-${to}`)?.focus();
  };
  return (
    <>
      <ol role="list" className="flex flex-col gap-3 overflow-x-auto pb-2 min-[900px]:flex-row" aria-label={`${sequence.name} screens`}>
        {sequence.steps.map((step, i) => (
          <li key={step.n} className="flex shrink-0 items-start gap-2">
            <button
              id={`aj-step-${i}`}
              type="button"
              onClick={() => setOpen(i)}
              onKeyDown={(e) => onKey(e, i)}
              className="flex w-40 flex-col gap-1 rounded-ks-md border border-ks-grey-4 bg-ks-surface p-2 text-left focus-visible:outline-2 focus-visible:outline-ks-purple"
            >
              <span className="text-xs text-ks-grey-1">{step.n}</span>
              <Thumb key={step.png_url} step={step} />
              <span className={step.frame_id || sequence.labels_withheld ? "text-sm text-ks-ink" : "text-sm text-ks-warn"}>
                {caption(step, sequence.labels_withheld)}
              </span>
              <span className="truncate text-xs text-ks-grey-1">{step.route}</span>
              <span className="text-xs text-ks-ink" title={step.tap ?? undefined}>
                {tapLine(step.tap)}
              </span>
            </button>
            {i < sequence.steps.length - 1 && (
              <span aria-hidden className="self-center text-ks-grey-1">
                →
              </span>
            )}
          </li>
        ))}
        {!sequence.ok && (
          <li className="flex w-40 shrink-0 flex-col justify-center rounded-ks-md border border-ks-danger p-2 text-sm text-ks-danger">
            walk stopped: {sequence.notes.join("; ") || "no note"}
          </li>
        )}
      </ol>
      <ScreenDialog sequence={sequence} index={open} onIndex={setOpen} />
    </>
  );
}

function Thumb({ step }: { step: Step }) {
  const [missing, setMissing] = useState(false);
  if (missing) {
    return (
      <span className="flex h-60 w-full items-center justify-center rounded-ks-md bg-ks-grey-4 text-xs text-ks-grey-1">
        {step.frame_id ?? step.page ?? step.route}
      </span>
    );
  }
  return (
    <img
      src={screenUrl(step.png_url)}
      alt={`${step.frame_id ?? step.page ?? step.route} ${step.frame_title ?? ""} screenshot`.trim()}
      loading="lazy"
      onError={() => setMissing(true)}
      className="h-60 w-full rounded-ks-md object-cover object-top"
    />
  );
}

function ScreenDialog({ sequence, index, onIndex }: { sequence: Sequence; index: number | null; onIndex: (i: number | null) => void }) {
  const step = index === null ? null : sequence.steps[index];
  const [missing, setMissing] = useState<string | null>(null);
  return (
    <Dialog open={step !== null} onOpenChange={(o) => !o && onIndex(null)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        {step && (
          <>
            <DialogHeader>
              <DialogTitle>{caption(step, sequence.labels_withheld)}</DialogTitle>
              <DialogDescription>
                Step {step.n} · {step.route}
              </DialogDescription>
            </DialogHeader>
            {missing === step.png_url ? (
              <p className="text-sm text-ks-grey-1">Thumbnail not available for this release</p>
            ) : (
              <img
                src={screenUrl(step.png_url)}
                alt={`${step.frame_id ?? step.page ?? step.route} screenshot`}
                onError={() => setMissing(step.png_url)}
                className="w-full rounded-ks-md"
              />
            )}
            <section className="flex flex-col gap-1 text-sm">
              <h3 className="font-medium text-ks-ink">Tappable</h3>
              <p className="text-ks-grey-1">{step.labels.length ? step.labels.join(" · ") : "–"}</p>
              <h3 className="font-medium text-ks-ink">Tap taken</h3>
              <p className="text-ks-grey-1">{step.tap ?? "end"}</p>
            </section>
            <div className="flex justify-between">
              <Button variant="secondary" size="sm" disabled={index === 0} onClick={() => onIndex((index ?? 0) - 1)}>
                Previous screen
              </Button>
              <Button variant="secondary" size="sm" disabled={index === sequence.steps.length - 1} onClick={() => onIndex((index ?? 0) + 1)}>
                Next screen
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
